/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

/**
 * Industry Utilities
 * Handles industry name standardization and mapping
 */

// Standard industry list with proper formatting
export const STANDARD_INDUSTRIES = [
  "Technology & IT",
  "Finance & Banking",
  "Healthcare & Medical",
  "Education & Training",
  "Retail & E-commerce",
  "Manufacturing & Production",
  "Construction & Real Estate",
  "Hospitality & Tourism",
  "Transportation & Logistics",
  "Media & Entertainment",
  "Telecommunications",
  "Agriculture & Farming",
  "Energy & Utilities",
  "Legal Services",
  "Consulting & Professional Services",
  "Consumer Goods",
  "FMCGs and CPGs",
  "Government & Public Sector",
  "Non-Profit & NGO",
  "Automotive",
  "Aerospace & Defense",
  "Pharmaceutical & Biotech",
  "Insurance",
  "Marketing & Advertising",
  "Human Resources",
  "Sales & Business Development",
  "Customer Service & Support",
  "Real Estate",
  "Food & Beverage",
  "Fashion & Apparel",
  "Sports & Fitness",
  "Recruitment & Staffing Agency",
  "Other"
];

// Legacy format mapping (for backward compatibility)
export const LEGACY_INDUSTRY_MAP = {
  // Old short format -> New full format
  "technology": "Technology & IT",
  "finance": "Finance & Banking",
  "healthcare": "Healthcare & Medical",
  "education": "Education & Training",
  "retail": "Retail & E-commerce",
  "manufacturing": "Manufacturing & Production",
  "construction": "Construction & Real Estate",
  "hospitality": "Hospitality & Tourism",
  "transportation": "Transportation & Logistics",
  "media": "Media & Entertainment",
  "telecommunications": "Telecommunications",
  "agriculture": "Agriculture & Farming",
  "energy": "Energy & Utilities",
  "legal": "Legal Services",
  "consulting": "Consulting & Professional Services",
  "consumer goods": "Consumer Goods",
  "fmcg": "FMCGs and CPGs",
  "cpg": "FMCGs and CPGs",
  "government": "Government & Public Sector",
  "ngo": "Non-Profit & NGO",
  "nonprofit": "Non-Profit & NGO",
  "automotive": "Automotive",
  "aerospace": "Aerospace & Defense",
  "pharmaceutical": "Pharmaceutical & Biotech",
  "insurance": "Insurance",
  "marketing": "Marketing & Advertising",
  "hr": "Human Resources",
  "sales": "Sales & Business Development",
  "customerservice": "Customer Service & Support",
  "other": "Other"
};

/**
 * Normalize industry name to standard format
 * Handles legacy formats and case variations
 * @param {string} industry - Industry name in any format
 * @returns {string} Standardized industry name
 */
export const normalizeIndustry = (industry) => {
  if (!industry) return '';
  
  // Remove extra whitespace and convert to lowercase for matching
  const cleaned = industry.trim();
  const lowerCase = cleaned.toLowerCase();
  
  // Check if it's already a standard format
  const exactMatch = STANDARD_INDUSTRIES.find(std => std.toLowerCase() === lowerCase);
  if (exactMatch) return exactMatch;
  
  // Check legacy mapping
  if (LEGACY_INDUSTRY_MAP[lowerCase]) {
    return LEGACY_INDUSTRY_MAP[lowerCase];
  }
  
  // Check partial matches (for variations)
  if (lowerCase.includes('tech') || lowerCase.includes('it')) return "Technology & IT";
  if (lowerCase.includes('financ') || lowerCase.includes('bank')) return "Finance & Banking";
  if (lowerCase.includes('health') || lowerCase.includes('medic')) return "Healthcare & Medical";
  if (lowerCase.includes('educat') || lowerCase.includes('train')) return "Education & Training";
  if (lowerCase.includes('retail') || lowerCase.includes('ecommerce') || lowerCase.includes('e-commerce')) return "Retail & E-commerce";
  if (lowerCase.includes('manufactur') || lowerCase.includes('production')) return "Manufacturing & Production";
  if (lowerCase.includes('construct') || lowerCase.includes('real estate')) return "Construction & Real Estate";
  if (lowerCase.includes('hospital') || lowerCase.includes('tourism')) return "Hospitality & Tourism";
  if (lowerCase.includes('transport') || lowerCase.includes('logistic')) return "Transportation & Logistics";
  if (lowerCase.includes('media') || lowerCase.includes('entertainment')) return "Media & Entertainment";
  if (lowerCase.includes('telecom')) return "Telecommunications";
  if (lowerCase.includes('agricult') || lowerCase.includes('farm')) return "Agriculture & Farming";
  if (lowerCase.includes('energy') || lowerCase.includes('utilit')) return "Energy & Utilities";
  if (lowerCase.includes('legal') || lowerCase.includes('law')) return "Legal Services";
  if (lowerCase.includes('consult') || lowerCase.includes('professional service')) return "Consulting & Professional Services";
  if (lowerCase.includes('consumer goods') && !lowerCase.includes('fmcg') && !lowerCase.includes('cpg')) return "Consumer Goods";
  if (lowerCase.includes('fmcg') || lowerCase.includes('cpg')) return "FMCGs and CPGs";
  if (lowerCase.includes('government') || lowerCase.includes('public sector')) return "Government & Public Sector";
  if (lowerCase.includes('ngo') || lowerCase.includes('non-profit') || lowerCase.includes('nonprofit')) return "Non-Profit & NGO";
  if (lowerCase.includes('auto')) return "Automotive";
  if (lowerCase.includes('aerospace') || lowerCase.includes('defense')) return "Aerospace & Defense";
  if (lowerCase.includes('pharma') || lowerCase.includes('biotech')) return "Pharmaceutical & Biotech";
  if (lowerCase.includes('insuran')) return "Insurance";
  if (lowerCase.includes('marketing') || lowerCase.includes('advertis')) return "Marketing & Advertising";
  if (lowerCase.includes('hr') || lowerCase.includes('human resource')) return "Human Resources";
  if (lowerCase.includes('sales') || lowerCase.includes('business development')) return "Sales & Business Development";
  if (lowerCase.includes('customer service') || lowerCase.includes('support')) return "Customer Service & Support";
  if (lowerCase.includes('food') || lowerCase.includes('beverage') || lowerCase.includes('restaurant')) return "Food & Beverage";
  if (lowerCase.includes('fashion') || lowerCase.includes('apparel') || lowerCase.includes('clothing')) return "Fashion & Apparel";
  if (lowerCase.includes('sport') || lowerCase.includes('fitness') || lowerCase.includes('gym')) return "Sports & Fitness";
  
  // If no match found, return the original (allows custom industries)
  return cleaned;
};

/**
 * Get industry display name
 * Ensures consistent display across the app
 * @param {string} industry - Industry value from database
 * @returns {string} Display-friendly industry name
 */
export const getIndustryDisplayName = (industry) => {
  return normalizeIndustry(industry) || 'Not Specified';
};

/**
 * Check if an industry value is valid
 * @param {string} industry - Industry value to check
 * @returns {boolean} True if valid
 */
export const isValidIndustry = (industry) => {
  if (!industry || typeof industry !== 'string') return false;
  return industry.trim().length > 0;
};

/**
 * Get industry color for badges/tags
 * @param {string} industry - Industry name
 * @returns {string} Color hex code
 */
export const getIndustryColor = (industry) => {
  const normalized = normalizeIndustry(industry);
  
  const colorMap = {
    "Technology & IT": "#3b82f6",
    "Finance & Banking": "#10b981",
    "Healthcare & Medical": "#ef4444",
    "Education & Training": "#8b5cf6",
    "Retail & E-commerce": "#f59e0b",
    "Manufacturing & Production": "#6b7280",
    "Construction & Real Estate": "#f97316",
    "Hospitality & Tourism": "#ec4899",
    "Transportation & Logistics": "#06b6d4",
    "Media & Entertainment": "#a855f7",
    "Telecommunications": "#14b8a6",
    "Agriculture & Farming": "#84cc16",
    "Energy & Utilities": "#eab308",
    "Legal Services": "#1e40af",
    "Consulting & Professional Services": "#059669",
    "Consumer Goods": "#ea580c",
    "FMCGs and CPGs": "#dc2626",
    "Government & Public Sector": "#4b5563",
    "Non-Profit & NGO": "#7c3aed",
    "Automotive": "#0891b2",
    "Aerospace & Defense": "#1e3a8a",
    "Pharmaceutical & Biotech": "#c026d3",
    "Insurance": "#0284c7",
    "Marketing & Advertising": "#f472b6",
    "Human Resources": "#7c2d12",
    "Sales & Business Development": "#15803d",
    "Customer Service & Support": "#0369a1",
    "Real Estate": "#b91c1c",
    "Food & Beverage": "#ca8a04",
    "Fashion & Apparel": "#be185d",
    "Sports & Fitness": "#16a34a"
  };
  
  return colorMap[normalized] || "#6b7280";
};

/**
 * Format industry for API submission
 * @param {string} industry - Industry value from form
 * @returns {string} Formatted industry value
 */
export const formatIndustryForSubmission = (industry) => {
  if (!industry) return '';
  return normalizeIndustry(industry);
};

/**
 * Get all unique industries from a list of items
 * @param {Array} items - Array of objects with industry property
 * @returns {Array} Sorted array of unique industries
 */
export const getUniqueIndustries = (items) => {
  if (!Array.isArray(items)) return [];
  
  const industries = new Set();
  items.forEach(item => {
    if (item.industry || item.jobIndustry) {
      const normalized = normalizeIndustry(item.industry || item.jobIndustry);
      if (normalized) industries.add(normalized);
    }
  });
  
  return Array.from(industries).sort();
};

const industryUtils = {
  STANDARD_INDUSTRIES,
  LEGACY_INDUSTRY_MAP,
  normalizeIndustry,
  getIndustryDisplayName,
  isValidIndustry,
  getIndustryColor,
  formatIndustryForSubmission,
  getUniqueIndustries
};

export default industryUtils;

